import { useState, useMemo } from 'react';
import { useTimeline } from '../hooks/useTimeline';

const stateColors: Record<string, string> = {
    COMPLETE: 'text-emerald-400',
    ERROR: 'text-rose-400',
    WAITING: 'text-amber-400',
    RETRYING: 'text-amber-300',
    RUNNING: 'text-sky-400',
    PLANNING: 'text-violet-300',
    STARTING: 'text-zinc-300',
};

export default function AgentTimelineDrawer({ sessionId }: { sessionId: string }) {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState<string>('ALL');
    const events = useTimeline(sessionId, 200);

    const filtered = useMemo(
        () => events.filter(e => filter === 'ALL' || e.state === filter),
        [events, filter]
    );

    return (
        <div className="mt-2">
            <button
                onClick={() => setOpen(o => !o)}
                className="text-xs px-2 py-1 rounded border border-zinc-800 bg-zinc-900/60 hover:bg-zinc-900"
            >
                {open ? 'Hide' : 'Show'} Timeline ({events.length})
            </button>

            {open && (
                <div className="mt-2 p-2 rounded border border-zinc-800 bg-zinc-900/60 max-h-72 overflow-auto">
                    <div className="flex gap-2 mb-2 items-center">
                        <span className="text-xs opacity-70">Filter:</span>
                        <select
                            className="text-xs bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                        >
                            {[
                                'ALL',
                                'STARTING',
                                'PLANNING',
                                'RUNNING',
                                'WAITING',
                                'RETRYING',
                                'COMPLETE',
                                'ERROR',
                            ].map(s => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>
                    <ul className="space-y-1">
                        {filtered.map((e, i) => (
                            <li key={i} className="text-xs flex gap-2">
                                <span
                                    className={`min-w-[84px] ${stateColors[e.state] ?? 'text-zinc-300'}`}
                                >
                                    {new Date(e.ts).toLocaleTimeString()} · {e.state}
                                </span>
                                {e.step ? (
                                    <span className="truncate">
                                        [{e.step.idx}/{e.step.total}] {e.step.title}
                                    </span>
                                ) : (
                                    <span className="truncate">{e.msg ?? ''}</span>
                                )}
                                {e.wait?.reason && (
                                    <span className="opacity-70">
                                        • wait {e.wait.reason}
                                        {e.wait.untilMs
                                            ? ` ${Math.round(e.wait.untilMs / 100) / 10}s`
                                            : ''}
                                    </span>
                                )}
                                {e.metrics?.elapsedMs && (
                                    <span className="opacity-70">
                                        • ⏱ {(e.metrics.elapsedMs / 1000).toFixed(1)}s
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
